import { Head, Link, usePage } from '@inertiajs/react'
import MainLayout from '../components/layout/MainLayout'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import { useState } from 'react'

/**
 * Pricing page - Tarifs et formules
 */
export default function Pricing() {
  const { pricing } = usePage().props
  const [openFaq, setOpenFaq] = useState(null)

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const faqs = [
    {
      question: 'Comment fonctionne le paiement par lead ?',
      answer: 'Vous ne payez que pour les leads qualifiés que vous recevez. Aucun abonnement mensuel, aucun engagement. Chaque lead est facturé selon son type (achat, vente, location, estimation).'
    },
    {
      question: 'Qu\'est-ce qu\'un lead qualifié ?',
      answer: 'Un lead qualifié est un contact qui a rempli le formulaire complet via notre IA conversationnelle, incluant ses coordonnées, son projet immobilier, son budget et sa timeline. Notre IA attribue un score de qualité à chaque lead.'
    },
    {
      question: 'Y a-t-il un nombre minimum de leads à acheter ?',
      answer: 'Non, aucun minimum requis. Vous pouvez commencer avec un seul lead. Nous facturons uniquement les leads que vous recevez effectivement.'
    },
    {
      question: 'Puis-je essayer gratuitement ?',
      answer: 'Oui ! Votre première inscription et la configuration de votre QR code sont gratuites. Vous ne payez que lorsque vous recevez vos premiers leads qualifiés.'
    },
    {
      question: 'Quels sont les avantages de la formule Premium ?',
      answer: 'La formule Premium à 149€/mois vous offre des tarifs préférentiels sur tous les types de leads (-20%), un support prioritaire, des analytics avancés, la personnalisation complète de l\'IA et l\'intégration avec votre CRM.'
    }
  ]

  return (
    <MainLayout>
      <Head title="Tarifs - SmartImmo">
        <meta name="description" content="Tarifs transparents pour la génération de leads immobiliers. Pay-per-lead uniquement, sans abonnement. Découvrez notre formule Premium." />
        <meta property="og:title" content="Tarifs - SmartImmo" />
        <meta property="og:description" content="Tarifs transparents pour la génération de leads immobiliers. Pay-per-lead uniquement, sans abonnement." />
      </Head>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tarification simple et transparente
          </h1>
          <p className="text-xl text-gray-600">
            Pay-per-lead uniquement. Pas d'abonnement caché. Pas d'engagement.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Pay-per-lead */}
            <Card>
              <Card.Body>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Pay-per-lead</h3>
                  <p className="text-gray-600">Payez uniquement pour les leads que vous recevez</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Achat</span>
                    <span className="text-2xl font-bold text-primary-600">{pricing.lead_prices.achat}€</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Vente</span>
                    <span className="text-2xl font-bold text-primary-600">{pricing.lead_prices.vente}€</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Location</span>
                    <span className="text-2xl font-bold text-primary-600">{pricing.lead_prices.location}€</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Estimation</span>
                    <span className="text-2xl font-bold text-primary-600">{pricing.lead_prices.estimation}€</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Aucun engagement</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Leads qualifiés par IA</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Dashboard de gestion</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Support par email</span>
                  </li>
                </ul>

                <Link href="/sign-up">
                  <Button variant="outline" className="w-full">
                    Commencer maintenant
                  </Button>
                </Link>
              </Card.Body>
            </Card>

            {/* Premium */}
            <Card className="border-2 border-primary-600 relative">
              <div className="absolute top-0 right-0 bg-primary-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                Recommandé
              </div>
              <Card.Body>
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">Premium</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-primary-600">{pricing.premium.price}€</span>
                    <span className="text-gray-600">/mois</span>
                  </div>
                  <p className="text-gray-600">+ tarifs préférentiels sur les leads (-20%)</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Achat</span>
                    <div>
                      <span className="text-sm text-gray-400 line-through mr-2">{pricing.lead_prices.achat}€</span>
                      <span className="text-2xl font-bold text-primary-600">{Math.round(pricing.lead_prices.achat * 0.8)}€</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Vente</span>
                    <div>
                      <span className="text-sm text-gray-400 line-through mr-2">{pricing.lead_prices.vente}€</span>
                      <span className="text-2xl font-bold text-primary-600">{Math.round(pricing.lead_prices.vente * 0.8)}€</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Location</span>
                    <div>
                      <span className="text-sm text-gray-400 line-through mr-2">{pricing.lead_prices.location}€</span>
                      <span className="text-2xl font-bold text-primary-600">{Math.round(pricing.lead_prices.location * 0.8)}€</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-700">Lead Estimation</span>
                    <div>
                      <span className="text-sm text-gray-400 line-through mr-2">{pricing.lead_prices.estimation}€</span>
                      <span className="text-2xl font-bold text-primary-600">{Math.round(pricing.lead_prices.estimation * 0.8)}€</span>
                    </div>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {pricing.premium.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link href="/sign-up">
                  <Button variant="primary" className="w-full">
                    Commencer maintenant
                  </Button>
                </Link>
              </Card.Body>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">Questions fréquentes</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <Card.Body>
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left flex justify-between items-start"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaq === index ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === index && (
                    <p className="mt-4 text-gray-600">{faq.answer}</p>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à commencer ?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Créez votre compte gratuitement et recevez vos premiers leads qualifiés dès aujourd'hui.
          </p>
          <Link href="/sign-up">
            <Button variant="secondary" size="lg">
              Créer mon compte
            </Button>
          </Link>
        </div>
      </section>
    </MainLayout>
  )
}
